import React, { memo, useCallback, useEffect, useId, useMemo, useState } from "react";
import useDesktopChecker from "hooks/useDesktopChecker";
import chevronDown from "assets/images/v2/chevron-down.svg";
import { LoadingPageProps } from "pages/dashboard/v2/loadingPageProps";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import { Spinner } from "components/v2/spinner/spinner";
import {
  LOAN_DETAILS_CARD_APR_LABEL,
  LOAN_DETAILS_CARD_ASSOCIATION_DUES_LABEL,
  LOAN_DETAILS_CARD_DOWN_PAYMENT_LABEL,
  LOAN_DETAILS_CARD_ESTIMATED_CLOSING_DATE_LABEL,
  LOAN_DETAILS_CARD_ESTIMATED_TOT_MON_PAYMENT_LABEL,
  LOAN_DETAILS_CARD_INSURANCE_LABEL,
  LOAN_DETAILS_CARD_INTEREST_RATE_LABEL,
  LOAN_DETAILS_CARD_INTEREST_RATE_LOCK_STATUS_LABEL,
  LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
  LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
  LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
  LOAN_DETAILS_CARD_LOAN_TERM_LABEL,
  LOAN_DETAILS_CARD_LOAN_TYPE_LABEL,
  LOAN_DETAILS_CARD_MORTGAGE_INSURANCE_LABEL,
  LOAN_DETAILS_CARD_OTHER_LABEL,
  LOAN_DETAILS_CARD_PRINCIPAL_INTEREST_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_TAX_LABEL,
  LOAN_DETAILS_CARD_PROPERTY_USE_LABEL,
  LOAN_DETAILS_CARD_PURCHASE_PRICE_LABEL,
  LOAN_DETAILS_CARD_RATE_AMORTIZATION_TYPE_LABEL,
  LOAN_DETAILS_CARD_RATE_LOCK_EXPIRATION_LABEL,
  LOAN_DETAILS_CARD_SUBORDINATE_FINANCING_LABEL,
  LOAN_DETAILS_CARD_SUPPLEMENTAL_PROPERTY_INSURANCE_LABEL,
  LOCKED,
  NOT_AVAILABLE,
  NOT_LOCKED,
  LOAN_DETAILS_CARD_PENDING_STATUS_TEXT,
} from "utilities/Constant";
import docErrorImg from "assets/images/v2/error-illustration.svg";
import styles from "./LoanDetails.module.scss";
import { useSelector } from "react-redux";
import { getCurrentUserId, getLoanPurpose, getNhDealApplicationId } from "features/authSlice";
import { ExtendedLoanDetails, useGetExtendedLoanDetailsQuery } from "pages/dashboard/home/dashboardService";
import { formatDollarAmount, formatLoanAmount } from "utilities/string";
import { skipToken } from '@reduxjs/toolkit/query';

interface LoanDetailsPageProps extends LoadingPageProps {
  readonly isLoanDetailsLoading?: boolean;
  readonly loanDetailsLoadError?: boolean;
}

const loanDetailTabs = Object.freeze([
  { label: "Overview", value: "overview" },
  { label: "Terms", value: "terms" },
  { label: "Dates", value: "dates" },
] as const);
type TabName = (typeof loanDetailTabs)[number]["value"];

interface KeyValueProps {
  id: string;
  label: string;
  value?: string | number;
  updatedValue?: boolean;
}

const KeyValue: React.FC<KeyValueProps> = memo(
  ({ id, label, value, updatedValue = false }) => (
    <div className={styles.keyValuePair} data-testid={`${id}-section`}>
      <div className={styles.keyValuePairWrapper}>
        <span
          className={styles.keyLabel}
          id={`${id}-label`}
          data-testid={`${id}-label`}
        >
          {label}
        </span>
      </div>

      <div className={styles.keyValuePairWrapper} data-testid={`${id}-value`} aria-live="polite">
        <span className={styles.keyValue} aria-labelledby={`${id}-label`}>
          {value ?? NOT_AVAILABLE}
        </span>
        {updatedValue && <span className={styles.updatedBadge}>Updated</span>}
      </div>
    </div>
  )
);
KeyValue.displayName = "KeyValue";

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="container">
    <div className="wrapper">
      <div className="content">{children}</div>
    </div>
  </div>
);

const loanPropertyFieldMappings = [
  ["purpose-", "LoanPurpose"],
  ["number-", "LoanNumber"],
  ["amount-", "LoanAmount"],
  ["price-", "PurchasePrice"],
  ["down-payment-", "TotalDownPaymentAmount"],
  ["address-", "PropertyLocation"],
  ["term-", "LoanAmortizationTermMonths"],
  ["type-", "MortgageType"],
  ["property-use-", "OccupancyType"],
  ["interest-rate-", "InitialInterestRate"],
  ["apr-", "DisclosedAprPercent"],
  ["lock-status-", "LoanIsLocked"],
  ["amortization-typ-", "LoanAmortizationType"],
  ["prin-ins-", "PrincipalAndInterestMonthlyPaymentAmount"],
  ["prop-tax-", "ProposedRealEstateTaxesAmount"],
  ["ins-", "ProposedHazardInsuranceAmount"],
  ["asso-dues-", "ProposedDuesAmount"],
  ["other-", "ProposedOtherAmount"],
  ["supp-prop-ins-", "SupplementalPropertyInsuranceAmount"],
  ["mort-ins-", "MortgageInsuranceAmount"],
  ["subordinate-financing-", "ProposedOtherMortgagesAmount"],
  ["total-monthly-payment-", "ProposedHousingExpenseTotal"],
  ["close-date-", "LoanScheduledClosingDate"],
  ["lock-exp-", "LockExpiresDate"],
] as const;

const getLoanSummaryFieldFromPropertyId = (field: string): string => {
  const matchedField = loanPropertyFieldMappings.find(([prefix]) => field.startsWith(prefix));
  return matchedField?.[1] ?? "";
};

const isLoanPropertyUpdated = (propertyId: string, updatedFields?: string[]): boolean => {
  if (!updatedFields) return false;

  const fieldName = getLoanSummaryFieldFromPropertyId(propertyId);
  return updatedFields.includes(fieldName);
}

const getLockStatusLabel = (loanIsLocked?: boolean | null): string => {
  if (loanIsLocked == null) return NOT_AVAILABLE;
  return loanIsLocked ? LOCKED : NOT_LOCKED;
};

const LoanDetails: React.FC<LoanDetailsPageProps> = ({
  onLoad,
  isLoanDetailsLoading,
  loanDetailsLoadError,
}) => {
  const loanPurpose = useSelector(getLoanPurpose);
  const isRefinance = useMemo(
    () =>
      loanPurpose?.toLowerCase().includes("refinance"),
    [loanPurpose]
  );

  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  const uid = useId().replaceAll(":", "");
  const [selectedTab, setSelectedTab] = useState<TabName>("overview");
  const [isHighlightedContentsExpanded, setIsHighlightedContentsExpanded] =
    useState(false);
  const isDesktop = useDesktopChecker();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);

  const {
    data: extendedLoanDetails,
    isLoading: isExtendedLoanDetailsLoading,
    isError: isExtendedLoanDetailsError,
  } = useGetExtendedLoanDetailsQuery(
    nhDealApplicationId && userId
      ? { nhDealApplicationId, userId }
      : skipToken
  );

  const toggleHighlightedContentsExpand = useCallback(() => {
    setIsHighlightedContentsExpanded((prev) => !prev);
  }, []);

  const formatPropertyAddress = (propertyAddress?: ExtendedLoanDetails): string => {
    if (!propertyAddress) return LOAN_DETAILS_CARD_PENDING_STATUS_TEXT;

    const { streetAddress, addressUnitDesignatorType, addressUnitIdentifier, city, state, postalCode } = propertyAddress;

    const isFieldEmpty = (value?: string | null) => !value || value.trim() === "";

    if (isFieldEmpty(streetAddress) && isFieldEmpty(addressUnitDesignatorType) && isFieldEmpty(addressUnitIdentifier) && isFieldEmpty(city) && isFieldEmpty(state) && isFieldEmpty(postalCode)) {
      return LOAN_DETAILS_CARD_PENDING_STATUS_TEXT;
    }

    const parts: string[] = [];
    let addressLine = isFieldEmpty(streetAddress) ? LOAN_DETAILS_CARD_PENDING_STATUS_TEXT : streetAddress!;

    const unitParts: string[] = [];
    if (!isFieldEmpty(addressUnitDesignatorType)) {
      unitParts.push(addressUnitDesignatorType!);
    }
    if (!isFieldEmpty(addressUnitIdentifier)) {
      unitParts.push(addressUnitIdentifier!);
    }
    if (unitParts.length > 0) {
      addressLine += ` ${unitParts.join(" ")}`;
    }

    parts.push(addressLine);

    if (!isFieldEmpty(city)) {
      parts.push(city!);
    }

    if (!isFieldEmpty(state) && !isFieldEmpty(postalCode)) {
      parts.push(`${state!} ${postalCode!}`);
    } else if (!isFieldEmpty(state)) {
      parts.push(state!);
    } else if (!isFieldEmpty(postalCode)) {
      parts.push(postalCode!);
    }

    return parts.join(", ");
  };

  const parseNumericValue = (value?: number | string | null): number | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isNaN(value) ? null : value;

    const parsedValue = Number(value.replaceAll(",", ""));
    return Number.isNaN(parsedValue) ? null : parsedValue;
  };

  // Format currency values
  const formatCurrency = (value?: number | null): string => {
    if (value === null || value === undefined) return NOT_AVAILABLE;
    return formatDollarAmount(value.toString()) ?? NOT_AVAILABLE;
  };

  // Format percentage values
  const formatPercentage = (value?: number | null): string => {
    if (value === null || value === undefined) return NOT_AVAILABLE;
    return `${value}%`;
  };

  const formatDownPayment = (
    downPaymentPercent?: number | null,
    downPaymentAmount?: number | null
  ): string => {
    if (downPaymentPercent !== null && downPaymentPercent !== undefined) {
      return formatPercentage(downPaymentPercent);
    }

    if (downPaymentAmount !== null && downPaymentAmount !== undefined) {
      return formatCurrency(downPaymentAmount);
    }

    return NOT_AVAILABLE;
  };

  // Format date values
  const formatDate = (dateString?: string): string => {
    if (!dateString) return NOT_AVAILABLE;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return NOT_AVAILABLE;
    }
  };

  // Format loan term
  const formatLoanTerm = (termInMonths?: number): string => {
    if (!termInMonths) return NOT_AVAILABLE;
    const years = termInMonths / 12;
    return years % 1 === 0 ? `${years} Years` : `${termInMonths} Months`;
  };

  // Build overview contents from loan summary data
  // If any field is missing/null/empty, display "Not Available"
  // Desktop order for overview
  const overviewContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(extendedLoanDetails);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: loanPurpose || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`purpose-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: extendedLoanDetails?.number || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`number-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatLoanAmount(extendedLoanDetails?.amount ?? null),
          updatedValue: isLoanPropertyUpdated(`amount-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `price-${uid}`,
          label: LOAN_DETAILS_CARD_PURCHASE_PRICE_LABEL,
          value: formatCurrency(extendedLoanDetails?.purchasePrice ?? null),
          updatedValue: isLoanPropertyUpdated(`price-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `down-payment-${uid}`,
          label: LOAN_DETAILS_CARD_DOWN_PAYMENT_LABEL,
          value: formatDownPayment(
            extendedLoanDetails?.downPaymentPercent,
            extendedLoanDetails?.downPayment
          ),
          updatedValue: isLoanPropertyUpdated(`down-payment-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted,
          updatedValue: isLoanPropertyUpdated(`address-${uid}`, extendedLoanDetails?.updatedFields)
        }
      ];
    },
    [uid, extendedLoanDetails, loanPurpose]
  );

  // Mobile order for overview
  const overviewMobileContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(extendedLoanDetails);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: loanPurpose || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`purpose-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: extendedLoanDetails?.number || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`number-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatLoanAmount(extendedLoanDetails?.amount ?? null),
          updatedValue: isLoanPropertyUpdated(`amount-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted,
          updatedValue: isLoanPropertyUpdated(`address-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `price-${uid}`,
          label: LOAN_DETAILS_CARD_PURCHASE_PRICE_LABEL,
          value: formatCurrency(extendedLoanDetails?.purchasePrice ?? null),
          updatedValue: isLoanPropertyUpdated(`price-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `down-payment-${uid}`,
          label: LOAN_DETAILS_CARD_DOWN_PAYMENT_LABEL,
          value: formatDownPayment(
            extendedLoanDetails?.downPaymentPercent,
            extendedLoanDetails?.downPayment
          ),
          updatedValue: isLoanPropertyUpdated(`down-payment-${uid}`, extendedLoanDetails?.updatedFields)
        }
      ];
    },
    [uid, extendedLoanDetails, loanPurpose]
  );

  // Desktop order for refinance
  const overviewRefinanceContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(extendedLoanDetails);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: loanPurpose || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`purpose-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: extendedLoanDetails?.number || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`number-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatLoanAmount(extendedLoanDetails?.amount ?? null),
          updatedValue: isLoanPropertyUpdated(`amount-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted,
          updatedValue: isLoanPropertyUpdated(`address-${uid}`, extendedLoanDetails?.updatedFields)
        }
      ];
    },
    [uid, extendedLoanDetails, loanPurpose]
  );

  // Mobile order for refinance
  const overviewRefinanceMobileContents = useMemo(
    () => {
      const propertyAddressFormatted = formatPropertyAddress(extendedLoanDetails);

      return [
        {
          id: `purpose-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
          value: loanPurpose || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`purpose-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `number-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
          value: extendedLoanDetails?.number || NOT_AVAILABLE,
          updatedValue: isLoanPropertyUpdated(`number-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `amount-${uid}`,
          label: LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
          value: formatLoanAmount(extendedLoanDetails?.amount ?? null),
          updatedValue: isLoanPropertyUpdated(`amount-${uid}`, extendedLoanDetails?.updatedFields)
        },
        {
          id: `address-${uid}`,
          label: LOAN_DETAILS_CARD_PROPERTY_ADDRESS_LABEL,
          value: propertyAddressFormatted,
          updatedValue: isLoanPropertyUpdated(`address-${uid}`, extendedLoanDetails?.updatedFields)
        }
      ];
    },
    [uid, extendedLoanDetails]
  );

  const termContents = useMemo(
    () => [
      {
        id: `term-${uid}`,
        label: LOAN_DETAILS_CARD_LOAN_TERM_LABEL,
        value: formatLoanTerm(extendedLoanDetails?.loanAmortizationTermMonths),
        updatedValue: isLoanPropertyUpdated(`term-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `type-${uid}`,
        label: LOAN_DETAILS_CARD_LOAN_TYPE_LABEL,
        value: extendedLoanDetails?.mortgageType ?? NOT_AVAILABLE,
        updatedValue: isLoanPropertyUpdated(`type-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `property-use-${uid}`,
        label: LOAN_DETAILS_CARD_PROPERTY_USE_LABEL,
        value: extendedLoanDetails?.occupancyType ?? NOT_AVAILABLE,
        updatedValue: isLoanPropertyUpdated(`property-use-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `interest-rate-${uid}`,
        label: LOAN_DETAILS_CARD_INTEREST_RATE_LABEL,
        value: formatPercentage(extendedLoanDetails?.initialInterestRate),
        updatedValue: isLoanPropertyUpdated(`interest-rate-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `apr-${uid}`,
        label: LOAN_DETAILS_CARD_APR_LABEL,
        value: formatPercentage(extendedLoanDetails?.aprPercent) ?? NOT_AVAILABLE,
        updatedValue: isLoanPropertyUpdated(`apr-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `lock-status-${uid}`,
        label: LOAN_DETAILS_CARD_INTEREST_RATE_LOCK_STATUS_LABEL,
        value: getLockStatusLabel(extendedLoanDetails?.loanIsLocked),
        updatedValue: isLoanPropertyUpdated(`lock-status-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `amortization-typ-${uid}`,
        label: LOAN_DETAILS_CARD_RATE_AMORTIZATION_TYPE_LABEL,
        value: extendedLoanDetails?.loanAmortizationType || NOT_AVAILABLE,
        updatedValue: isLoanPropertyUpdated(`amortization-typ-${uid}`, extendedLoanDetails?.updatedFields)
      }
    ],
    [uid, extendedLoanDetails]
  );

  const termHighlightedContents = useMemo(
    () => [
      {
        id: `prin-ins-${uid}`,
        label: LOAN_DETAILS_CARD_PRINCIPAL_INTEREST_LABEL,
        value: formatCurrency(extendedLoanDetails?.principalAndInterestMonthlyPaymentAmount),
        updatedValue: isLoanPropertyUpdated(`prin-ins-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `prop-tax-${uid}`,
        label: LOAN_DETAILS_CARD_PROPERTY_TAX_LABEL,
        value: formatCurrency(parseNumericValue(extendedLoanDetails?.proposedRealEstateTaxesAmount)),
        updatedValue: isLoanPropertyUpdated(`prop-tax-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `ins-${uid}`,
        label: LOAN_DETAILS_CARD_INSURANCE_LABEL,
        value: formatCurrency(parseNumericValue(extendedLoanDetails?.proposedHazardInsuranceAmount)),
        updatedValue: isLoanPropertyUpdated(`ins-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `asso-dues-${uid}`,
        label: LOAN_DETAILS_CARD_ASSOCIATION_DUES_LABEL,
        value: formatCurrency(parseNumericValue(extendedLoanDetails?.proposedDuesAmount)),
        updatedValue: isLoanPropertyUpdated(`asso-dues-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `other-${uid}`,
        label: LOAN_DETAILS_CARD_OTHER_LABEL,
        value: formatCurrency(extendedLoanDetails?.proposedOtherAmount),
        updatedValue: isLoanPropertyUpdated(`other-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `supp-prop-ins-${uid}`,
        label: LOAN_DETAILS_CARD_SUPPLEMENTAL_PROPERTY_INSURANCE_LABEL,
        value: formatCurrency(extendedLoanDetails?.supplementalPropertyInsuranceAmount),
        updatedValue: isLoanPropertyUpdated(`supp-prop-ins-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `mort-ins-${uid}`,
        label: LOAN_DETAILS_CARD_MORTGAGE_INSURANCE_LABEL,
        value: formatCurrency(parseNumericValue(extendedLoanDetails?.mortgageInsuranceAmount)),
        updatedValue: isLoanPropertyUpdated(`mort-ins-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `subordinate-financing-${uid}`,
        label: LOAN_DETAILS_CARD_SUBORDINATE_FINANCING_LABEL,
        value: formatCurrency(extendedLoanDetails?.proposedOtherMortgagesAmount),
        updatedValue: isLoanPropertyUpdated(`subordinate-financing-${uid}`, extendedLoanDetails?.updatedFields)
      },
    ],
    [uid, extendedLoanDetails]
  );

  const dateContents = useMemo(
    () => [
      {
        id: `close-date-${uid}`,
        label: LOAN_DETAILS_CARD_ESTIMATED_CLOSING_DATE_LABEL,
        value: formatDate(extendedLoanDetails?.estimatedClosingDate),
        updatedValue: isLoanPropertyUpdated(`close-date-${uid}`, extendedLoanDetails?.updatedFields)
      },
      {
        id: `lock-exp-${uid}`,
        label: LOAN_DETAILS_CARD_RATE_LOCK_EXPIRATION_LABEL,
        value: formatDate(extendedLoanDetails?.rateLockExpirationDate),
        updatedValue: isLoanPropertyUpdated(`lock-exp-${uid}`, extendedLoanDetails?.updatedFields)
      }
    ],
    [uid, extendedLoanDetails]
  );

  // Extract nested ternary operation and use positive condition
  let overviewContent: typeof overviewContents;

  if (isRefinance) {
    overviewContent = isDesktop ? overviewRefinanceContents : overviewRefinanceMobileContents;
  } else {
    overviewContent = isDesktop ? overviewContents : overviewMobileContents;
  }

  const loanDetailTabContents: Record<TabName, typeof overviewContents> = {
    overview: overviewContent,
    terms: termContents,
    dates: dateContents,
  };

  const selectedTabContents = loanDetailTabContents[selectedTab];

  // Combined loading state
  const isLoading = isLoanDetailsLoading || isExtendedLoanDetailsLoading;

  // Combined error state
  const hasError = loanDetailsLoadError || isExtendedLoanDetailsError;

  // ---- Spinner
  if (isLoading) {
    return (
      <PageShell>
        <div className={`${styles.spinner} ${styles.loadingContainer}`}>
          <Spinner isApi />
          <div className={styles.loadingText}>
            <div className={styles.loadingTitle}>Loading Loan Details</div>
            <div className={styles.loadingSubtitle}>
              This will take a moment!
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- Error
  if (hasError) {
    return (
      <PageShell>
        <div className={styles.previewError}>
          <img
            src={docErrorImg}
            alt="Error loading loan details"
            className={styles.errorImage}
          />
          <div className={styles.errorText}>
            <div className={styles.errorTitle}>Something went wrong</div>
            <div>
              We are having issues loading this page, Please try again later.
            </div>
            <div>We apologize for the inconvenience!</div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- Loan Details
  return (
    <div className={styles.content}>
      <div className={styles.loanDetails}>Loan Details</div>
      <div className={styles.hereCurrentLoanDetails}>
        Here are your current loan application details. This information is for
        reference only and is subject to change.
      </div>

      <TogglePills
        options={loanDetailTabs}
        value={selectedTab}
        onChange={setSelectedTab}
      />

      <div className={styles.loanDetailCard}>
        <div className={styles.cardBody}>
          <div className={styles.keyValueSectionWrapper}>
            {selectedTabContents.map((content) => (
              <KeyValue
                key={content.id}
                id={content.id}
                label={content.label}
                value={content.value}
                updatedValue={content.updatedValue ?? isLoanPropertyUpdated(content.id, extendedLoanDetails?.updatedFields)}
              />
            ))}
          </div>
        </div>

        {/* Highlighted section - ONLY for Terms tab */}
        {selectedTab === "terms" && (
          <div className={styles.highlightSection}>
            <button
              type="button"
              className={styles.highlightHeader}
              onClick={toggleHighlightedContentsExpand}
              aria-expanded={isHighlightedContentsExpanded}
            >
              <div className={styles.labelRow}>
                {LOAN_DETAILS_CARD_ESTIMATED_TOT_MON_PAYMENT_LABEL}
              </div>
              <div className={styles.valueRow}>
                <div className={styles.highlightKeyValue}>
                  {formatCurrency(extendedLoanDetails?.proposedHousingExpenseTotal)}
                </div>
                {isLoanPropertyUpdated(`total-monthly-payment-${uid}`, extendedLoanDetails?.updatedFields) && <span className={styles.updatedBadge}>Updated</span>}
                <img
                  className={`${styles.chevron} ${isHighlightedContentsExpanded ? styles.expanded : ""
                    }`}
                  src={chevronDown}
                  alt={isHighlightedContentsExpanded ? "Collapse" : "Expand"}
                />
              </div>
            </button>
            <div
              className={`${styles.highlightDivider} ${isHighlightedContentsExpanded ? styles.showDivider : ""
                }`}
            />
            <div
              className={`${styles.collapsibleBody} ${isHighlightedContentsExpanded ? styles.expanded : ""
                }`}
            >
              <div className={styles.highlightBody}>
                <div className={styles.keyValueSectionWrapper}>
                  {termHighlightedContents.map((content) => (
                    <KeyValue
                      key={content.id}
                      id={content.id}
                      label={content.label}
                      value={content.value}
                      updatedValue={content.updatedValue}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanDetails;